import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Dropdown, Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faCommentDots,
  faEllipsis,
  faPhone,
  faStore,
} from "@fortawesome/free-solid-svg-icons";
import Swal from "sweetalert2";
import { crmPostventaV2Api } from "../crmpostventa-v2/crmPostventaV2Api";
import ProcessActionModal from "../crmpostventa-v2/components/ProcessActionModal";
import ProcessQueueTable from "../crmpostventa-v2/components/ProcessQueueTable";
import ProcessStatusBadge from "../crmpostventa-v2/components/ProcessStatusBadge";
import { getSlugByProcessCode } from "../crmpostventa-v2/processCatalog";
import ZocoCheckAlertsSection from "./zoco-check-alerts/ZocoCheckAlertsSection";

const defaultState = {
  loading: true,
  rows: [],
  total: 0,
  permissions: null,
  error: "",
};

const normalizeBlockError = (error) => {
  const message = String(error?.message || "").trim();
  if (message) {
    console.error("Detalle tecnico cola embebida:", message);
  }

  return "No se pudo cargar esta seccion.";
};

const formatCurrency = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return "$0";
  }

  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    maximumFractionDigits: 0,
  }).format(numeric);
};

const formatMetricValue = (value, fallback = "-") => {
  if (value === undefined || value === null || value === "") {
    return fallback;
  }

  return value;
};

function BillingAlertsCards({
  rows,
  loading,
  submitting,
  searchValue,
  onSearchChange,
  onManage,
  onTake,
  onAssign,
  onRelease,
  emptyMessage,
}) {
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    setExpandedId((current) => {
      if (!rows.length) {
        return null;
      }

      return rows.some((item) => item.id === current) ? current : rows[0].id;
    });
  }, [rows]);

  const filteredRows = useMemo(() => {
    const term = String(searchValue || "").trim().toLowerCase();
    if (!term) {
      return rows;
    }

    return rows.filter((item) =>
      [
        item.comercio,
        item.cuit,
        item.numeroTerminal,
        item.numeroComercio,
        item.responsable,
        item.telefono,
        item.provincia,
        item.estadoLabel,
        item.estadoActual,
        item.accionPermitida,
        item.mensajeSugerido,
        item.resumen,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term))
    );
  }, [rows, searchValue]);

  return (
    <div className="crm-postventa-alert-queue crm-postventa-alert-queue--billing">
      <div className="crm-alert-search-wrap crm-alert-search-wrap--billing">
        <FontAwesomeIcon icon={faStore} />
        <input
          type="search"
          className="form-control crm-postventa-filter-control"
          placeholder="Buscar por comercio, CUIT, terminal, responsable, telefono..."
          value={searchValue}
          onChange={(event) => onSearchChange(event.target.value)}
          aria-label="Buscar alertas de facturacion"
        />
      </div>

      {loading ? (
        <div className="py-4 text-center">
          <Spinner animation="border" size="sm" className="me-2" />
          Cargando alertas de facturacion...
        </div>
      ) : !filteredRows.length ? (
        <Alert variant="secondary" className="mb-0">
          {searchValue ? "No hay alertas que coincidan con la búsqueda." : emptyMessage}
        </Alert>
      ) : (
        <div className="crm-alert-billing-card-list">
          {filteredRows.map((item) => {
            const isExpanded = expandedId === item.id;
            const actionLabel = item.esSoloLectura || item.puedeGestionar === false ? "Ver detalle" : "Gestionar";

            return (
              <article className={`crm-alert-billing-card ${isExpanded ? "is-expanded" : ""}`} key={`${item.procesoCodigo}-${item.id}`}>
                <div className="crm-alert-billing-card-head">
                  <button
                    type="button"
                    className="crm-alert-billing-card-toggle"
                    onClick={() => setExpandedId((current) => (current === item.id ? null : item.id))}
                    aria-expanded={isExpanded}
                  >
                    <div className="crm-alert-billing-card-main">
                      <strong>{item.comercio || "Sin comercio"}</strong>
                      <div className="crm-alert-billing-card-meta">
                        <span>CUIT: {item.cuit || "-"}</span>
                        <span>Terminal: {item.numeroTerminal || "-"}</span>
                        <span>Provincia: {item.provincia || "-"}</span>
                        <span>Teléfono: {item.telefono || "-"}</span>
                      </div>
                      <p className="crm-alert-billing-card-summary">
                        {item.resumen || item.motivoPrioridad || item.accionPermitida || "Sin contexto adicional."}
                      </p>
                    </div>
                  </button>

                  <div className="crm-alert-billing-card-actions">
                    <ProcessStatusBadge>{item.estadoLabel || item.estadoActual || "Pendiente"}</ProcessStatusBadge>
                    <span className="crm-alert-billing-chip crm-alert-billing-chip--score">Score {item.scorePrioridad || 0}</span>
                    <span className="crm-alert-billing-chip crm-alert-billing-chip--priority">{item.prioridad || "Media"}</span>
                    <Dropdown align="end" onClick={(event) => event.stopPropagation()}>
                      <Dropdown.Toggle
                        variant="outline-secondary"
                        size="sm"
                        className="crm-alert-billing-dropdown"
                        disabled={submitting}
                      >
                        <span>Acciones</span>
                        <FontAwesomeIcon icon={faEllipsis} />
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        {item.puedeTomar ? (
                          <Dropdown.Item onClick={() => onTake?.(item)}>Tomar</Dropdown.Item>
                        ) : null}
                        {item.puedeAsignar ? (
                          <Dropdown.Item onClick={() => onAssign?.(item)}>Asignar</Dropdown.Item>
                        ) : null}
                        {item.puedeLiberar ? (
                          <Dropdown.Item onClick={() => onRelease?.(item)}>Liberar</Dropdown.Item>
                        ) : null}
                        <Dropdown.Item onClick={() => onManage?.(item)}>{actionLabel}</Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                    <button
                      type="button"
                      className="crm-alert-billing-chevron-button"
                      onClick={() => setExpandedId((current) => (current === item.id ? null : item.id))}
                      aria-label={isExpanded ? "Colapsar alerta" : "Expandir alerta"}
                      aria-expanded={isExpanded}
                    >
                      <FontAwesomeIcon icon={faChevronDown} className={`crm-alert-billing-chevron ${isExpanded ? "is-open" : ""}`} />
                    </button>
                  </div>
                </div>

                {isExpanded ? (
                  <div className="crm-alert-billing-card-body">
                    <div className="crm-alert-billing-highlight">
                      {item.tipoAlerta || item.accionPermitida || "Seguimiento operativo pendiente."}
                    </div>

                    <div className="crm-alert-billing-grid">
                      <div className="crm-alert-billing-panel">
                        <div className="crm-alert-billing-panel-title">
                          <FontAwesomeIcon icon={faStore} />
                          <span>Contexto operativo</span>
                        </div>
                        <div className="crm-alert-billing-detail-grid">
                          <div>
                            <span>Acción sugerida</span>
                            <strong>{formatMetricValue(item.accionPermitida || item.accionSugerida)}</strong>
                          </div>
                          <div>
                            <span>Hoy</span>
                            <strong>{formatCurrency(item.montoHoy)}</strong>
                          </div>
                          <div>
                            <span>Patrón detectado</span>
                            <strong>{formatMetricValue(item.patronFacturacion, "Sin patrón")}</strong>
                          </div>
                          <div>
                            <span>Prom. 7 días</span>
                            <strong>{formatCurrency(item.promedio7Dias)}</strong>
                          </div>
                          <div>
                            <span>Último contacto</span>
                            <strong>{formatMetricValue(item.ultimaGestion)}</strong>
                          </div>
                          <div>
                            <span>Variación</span>
                            <strong>{formatMetricValue(item.variacion, "-")}</strong>
                          </div>
                          <div>
                            <span>Próximo contacto</span>
                            <strong>{formatMetricValue(item.fechaProximaAccion)}</strong>
                          </div>
                          <div>
                            <span>Responsable</span>
                            <strong>{formatMetricValue(item.responsable, "Sin asignar")}</strong>
                          </div>
                        </div>
                      </div>

                      <div className="crm-alert-billing-panel">
                        <div className="crm-alert-billing-panel-title">
                          <FontAwesomeIcon icon={faCommentDots} />
                          <span>Mensaje sugerido</span>
                        </div>
                        <p className="crm-alert-billing-message">
                          {item.mensajeSugerido && item.mensajeSugerido !== "-" ? item.mensajeSugerido : "Sin mensaje sugerido disponible."}
                        </p>
                        <div className="crm-alert-billing-footer">
                          <span className="crm-alert-billing-phone">
                            <FontAwesomeIcon icon={faPhone} />
                            {item.telefono || "Sin teléfono"}
                          </span>
                          <Button
                            size="sm"
                            variant={item.esSoloLectura || item.puedeGestionar === false ? "outline-dark" : "success"}
                            onClick={() => onManage?.(item)}
                            disabled={submitting}
                          >
                            {actionLabel}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function EmbeddedProcessQueue({
  title,
  processCode,
  processSlug,
  viewAllPath,
  maxRows = 5,
  filters = {},
  emptyMessage,
  hideContainerCard = false,
  showTitle = true,
  showRefresh = true,
  showActions = true,
  className = "",
}) {
  const [state, setState] = useState(defaultState);
  const [detail, setDetail] = useState(null);
  const [existingEvidences, setExistingEvidences] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [responsables, setResponsables] = useState([]);
  const [localSearch, setLocalSearch] = useState("");
  const [initialResult, setInitialResult] = useState("");
  const [mutationNonce, setMutationNonce] = useState(0);

  const isBillingAlertsSection = processCode === "TERMINAL_SIN_FACTURACION_V2" && title === "Alertas de facturacion";
  const isZocoCheckAlertsSection = processCode === "ZOCO_CHECK_V2" && title === "Zoco Check";

  const requestParams = useMemo(
    () => ({
      procesoCodigo: processCode,
      fechaReferencia: filters.fechaReferencia,
      prioridad: filters.prioridad,
      estado: filters.estado,
      texto: filters.texto,
      pagina: 1,
      tamanoPagina: maxRows,
    }),
    [filters.estado, filters.fechaReferencia, filters.prioridad, filters.texto, maxRows, processCode]
  );

  const loadQueue = async () => {
    setState((prev) => ({ ...prev, loading: true, error: "" }));
    try {
      const response = await crmPostventaV2Api.obtenerAlertas(requestParams);
      setState({
        loading: false,
        rows: response?.filas || [],
        total: response?.total || 0,
        permissions: response?.permisos || null,
        error: "",
      });
      setResponsables(response?.responsables || []);
    } catch (error) {
      console.error(`Error cargando cola embebida ${processCode}`, error);
      setState({
        loading: false,
        rows: [],
        total: 0,
        permissions: null,
        error: normalizeBlockError(error),
      });
      setResponsables([]);
    }
  };

  useEffect(() => {
    loadQueue();
  }, [requestParams]);

  const openDetail = async (item, options = {}) => {
    try {
      setSubmitting(true);
      setInitialResult(options.initialResult || "");
      const resolvedSlug = item.procesoSlug || processSlug || getSlugByProcessCode(item.procesoCodigo || processCode);
      const [detailResponse, evidencesResponse] = await Promise.all([
        crmPostventaV2Api.obtenerDetalle(resolvedSlug, item.id),
        crmPostventaV2Api.obtenerEvidencias(item.id),
      ]);

      setDetail({
        ...detailResponse,
        instancia: {
          ...detailResponse?.instancia,
          procesoSlug: resolvedSlug,
        },
      });
      setExistingEvidences(evidencesResponse || []);
      setModalOpen(true);
    } catch (error) {
      Swal.fire(title, error.message, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleOwnershipAction = async (action, item, body = {}) => {
    try {
      setSubmitting(true);
      const versionToken = body.versionToken || item.versionToken;
      if (action === "take") {
        await crmPostventaV2Api.tomarProceso(item.id, {
          versionToken,
          motivo: body.motivo || "Toma desde tablero de alertas V2.",
        });
      } else if (action === "assign") {
        await crmPostventaV2Api.asignarProceso(item.id, {
          versionToken,
          usuarioId: body.usuarioId,
          motivo: body.motivo || "Asignacion desde tablero de alertas V2.",
        });
      } else if (action === "release") {
        await crmPostventaV2Api.liberarProceso(item.id, {
          versionToken,
          motivo: body.motivo || "Liberacion desde tablero de alertas V2.",
        });
      }

      await loadQueue();
      setMutationNonce((prev) => prev + 1);
    } catch (error) {
      Swal.fire(title, error.message, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAssign = async (item) => {
    const options = {};
    responsables.forEach((responsable) => {
      options[String(responsable.usuarioId)] = `${responsable.nombre} (${responsable.total})`;
    });

    if (!Object.keys(options).length) {
      Swal.fire(title, "Todavia no hay responsables visibles para asignar desde esta grilla.", "info");
      return;
    }

    const { isConfirmed, value } = await Swal.fire({
      title: "Asignar responsable",
      input: "select",
      inputOptions: options,
      inputPlaceholder: "Seleccionar responsable",
      showCancelButton: true,
      confirmButtonText: "Asignar",
      cancelButtonText: "Cancelar",
      inputValidator: (selected) => (!selected ? "Selecciona un responsable." : undefined),
    });

    if (!isConfirmed || !value) {
      return;
    }

    await handleOwnershipAction("assign", item, { usuarioId: Number(value) });
  };

  const handleRelease = async (item) => {
    const confirm = await Swal.fire({
      title: "Liberar proceso",
      text: "El proceso quedara nuevamente sin responsable.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Liberar",
      cancelButtonText: "Cancelar",
    });

    if (!confirm.isConfirmed) {
      return;
    }

    await handleOwnershipAction("release", item);
  };

  const handleSubmitAction = async (payload) => {
    if (!detail?.instancia?.id) {
      return;
    }

    try {
      setSubmitting(true);
      const response = await crmPostventaV2Api.ejecutarAccion(detail.instancia.procesoSlug, detail.instancia.id, payload);
      await Swal.fire(title, response?.message || "Accion registrada.", "success");
      setModalOpen(false);
      setDetail(null);
      setExistingEvidences([]);
      setInitialResult("");
      await loadQueue();
      setMutationNonce((prev) => prev + 1);
    } catch (error) {
      Swal.fire(title, error.message, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const content = (
    <div className={`crm-postventa-alert-queue ${className}`.trim()}>
      {showTitle && !isZocoCheckAlertsSection ? (
        <div className="crm-alert-section-header">
          <div>
            <h3>{title}</h3>
          </div>
          <div className="crm-alert-accordion-head-badges">
            <span className="crm-alert-total-pill">{state.total || 0} alertas</span>
            {showRefresh ? (
              <Button variant="outline-secondary" size="sm" onClick={loadQueue} disabled={state.loading || submitting}>
                Actualizar
              </Button>
            ) : null}
            <Link className="btn btn-success btn-sm" to={viewAllPath}>
              Ver todos
            </Link>
          </div>
        </div>
      ) : null}

      {isZocoCheckAlertsSection ? (
        <ZocoCheckAlertsSection
          title={title}
          processCode={processCode}
          processSlug={processSlug}
          globalFilters={filters}
          emptyMessage={emptyMessage}
          onManage={openDetail}
          onAssign={handleAssign}
          onRelease={handleRelease}
          onTake={(item) => handleOwnershipAction("take", item)}
          onMutationNonce={mutationNonce}
        />
      ) : state.loading ? (
        <div className="py-4 text-center">
          <Spinner animation="border" size="sm" className="me-2" />
          Cargando {title.toLowerCase()}...
        </div>
      ) : state.error ? (
        <Alert variant="warning" className="mb-0">
          {state.error}
        </Alert>
      ) : isBillingAlertsSection ? (
        <BillingAlertsCards
          rows={state.rows}
          loading={state.loading}
          submitting={submitting}
          searchValue={localSearch}
          onSearchChange={setLocalSearch}
          onManage={openDetail}
          onTake={(item) => handleOwnershipAction("take", item)}
          onAssign={handleAssign}
          onRelease={handleRelease}
          emptyMessage={emptyMessage || `No hay registros activos para ${title.toLowerCase()}.`}
        />
      ) : (
        <div className="crm-postventa-table-wrap-alertas">
          <div className="crm-postventa-table-scroll-x crm-postventa-table-scroll-alertas">
            <ProcessQueueTable
              rows={state.rows}
              onManage={openDetail}
              onTake={(item) => handleOwnershipAction("take", item)}
              onAssign={handleAssign}
              onRelease={handleRelease}
              loading={submitting}
              compact
              showActions={showActions}
              emptyMessage={emptyMessage || `No hay registros activos para ${title.toLowerCase()}.`}
              className="crm-postventa-table crm-postventa-table-alertas"
              containerClassName=""
            />
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      {hideContainerCard ? content : (
        <section className="crm-postventa-v2-panel p-4 crm-postventa-v2-embedded-queue">
          {content}
        </section>
      )}

      <ProcessActionModal
        show={modalOpen}
        onHide={() => {
          setModalOpen(false);
          setInitialResult("");
        }}
        detail={detail}
        existingEvidences={existingEvidences}
        submitting={submitting}
        initialResult={initialResult}
        onSubmit={handleSubmitAction}
      />
    </>
  );
}
